(ns leg.dialogs
  "Every field somebody types into, in a window laid over the one they were
  on.

  A form is a small job with a beginning and an end: it is opened, filled in,
  and either done or abandoned. A dialog says all three - it takes the focus,
  it has one obvious way out, and the page underneath is still there when it
  closes, unchanged if the answer was Cancel. A field sitting in a page says
  none of it: it is always open, it is never finished, and it saves when
  somebody looks away.

  So: adding a feed, renaming one, adding a reader. Two of them have no
  field at all - emptying the collection, and marking a feed read - and they
  are here for the other half of the same reason: a job with a beginning and
  an end, where the end is a press that cannot be taken back. A dialog with
  no field is still a form; what it asks for is a choice rather than a word.

  What is *not* here is the key bindings, which are fields that take a key
  rather than text, and the file chooser, which opens a window of the
  operating system's own.

  Which one is open is `:dialog` in the state and not an address (see
  `leg.state/dialog!`) - a dialog is a sentence said about the row that was
  pressed, not a place to come back to."
  (:require-macros [kc.core :refer [def-ui $]])
  (:require [clojure.string :as str]
            [kc.core :as kc]
            [leg.mui :as mui :refer [sx]]
            [leg.state :as state]
            [leg.util :as u]))

(defn- close! [] (state/dialog! nil))

(defn- address?
  "The same shallow check the login screen makes, and for the same reason:
  the only proof that an address works is a code that comes back from it."
  [s]
  (boolean (re-matches #"[^@\s]+@[^@\s.]+\.[^@\s]+" (str/trim (or s "")))))

(def-ui Frame
  "What every one of them looks like: a title, the form, and the row of
  answers at the foot with the doing one on the right."
  [title ***]
  ($ mui/Dialog {:open true :onClose (fn [_] (close!))}
     ($ mui/DialogTitle title)
     ***))

(def-ui Body
  "The form itself, as a column. The padding at the top is for the labels of
  the fields, which sit on the line the content begins at."
  [***]
  ($ mui/DialogContent
     ($ mui/Box {:sx (sx {:display "flex" :flexDirection "column" :gap "1rem"
                          :paddingTop ".5rem"})}
        ***)))

(def-ui Note
  "A line of prose under a field: what it is for, or what just happened."
  [***]
  ($ mui/Typography {:sx (sx {:color "text.secondary" :fontSize ".8rem"
                              :overflowWrap "anywhere"})}
     ***))

;;; -------------------------------------------------------------- add feed

(def-ui AddFeed
  "Subscribing. The address of the feed, or the address of the site - the
  server looks for the feed in the page when it is handed one, because
  nobody should have to find a feed URL by hand in 2026.

  It closes itself: adding leaves the settings page and opens that feed
  (`leg.state/add-feed!`), because what was asked for is the feed."
  [busy?]
  (let [[url set-url] (kc/use-state "")
        ok?     (and (not busy?) (not (str/blank? url)))
        submit! (fn [] (when ok? (state/add-feed! (str/trim url))))]
    ($ Frame {:title "Add feed"}
       ($ Body
          ($ mui/TextField {:label "Address"
                            :placeholder "example.com or example.com/feed.xml"
                            :value url
                            :size "small"
                            :fullWidth true
                            :autoFocus true
                            :slotProps (sx {:htmlInput {:autoCapitalize "none"
                                                        :spellCheck false}})
                            :onChange (fn [^js e] (set-url (-> e .-target .-value)))
                            :onKeyDown (fn [^js e]
                                         (when (= "Enter" (.-key e))
                                           (.preventDefault e)
                                           (submit!)))})
          ($ Note (if busy?
                    "Fetching the feed..."
                    "The site will do - the feed is looked for in the page.")))
       ($ mui/DialogActions
          ($ mui/Button {:onClick (fn [_] (close!))} "Cancel")
          ($ mui/Button {:variant "contained" :disableElevation true
                         :disabled (not ok?)
                         :onClick (fn [_] (submit!))}
             "Add")))))

;;; ------------------------------------------------------------- one feed

(def-ui Feed
  "One subscription: what it is called here, which group it is in, and the
  way out of it.

  The name is what *this* person calls the feed - whoever else reads the same
  feed reads it under its own name - and an empty field hands the feed's own
  title back, which is why it is sent as an empty string rather than left
  out. The group is the same kind of fact and behaves the same way: empty
  takes the feed out of the group, which is the only way out there is.

  Typing a group that does not exist yet is how one is made. There is no
  screen for making them and nothing to tidy up afterwards - a group is the
  word written here and it lasts exactly as long as a feed carries it. The
  `datalist` under the field is the ones already in use, so the second feed
  in a group is picked rather than spelled again: two spellings would be two
  groups, and this is the one thing about a typed-in name worth defending
  against.

  [Remove] is two presses: the first turns the button into the question and
  the second answers it, so the sentence somebody reads is on the thing they
  are about to do. Nothing is deleted from the server by it - the articles
  stay where they are, and subscribing again brings the feed back with them."
  [feed groups]
  (let [id                (:feed/id feed)
        given             (or (:feed/title feed) "")
        given-group       (or (:feed/group feed) "")
        [title set-title] (kc/use-state given)
        [group set-group] (kc/use-state given-group)
        [sure? set-sure]  (kc/use-state false)
        save! (fn []
                (when (or (not= (str/trim title) given)
                          (not= (str/trim group) given-group))
                  (state/save-feed! id {:title (str/trim title)
                                        :group (str/trim group)}))
                (close!))]
    ($ Frame {:title "Feed"}
       ($ Body
          ($ mui/TextField {:label "Title"
                            :value title
                            :size "small"
                            :fullWidth true
                            :autoFocus true
                            :placeholder (u/host (:feed/url feed))
                            :slotProps (sx {:htmlInput {:spellCheck false}})
                            :onChange (fn [^js e] (set-title (-> e .-target .-value)))
                            :onKeyDown (fn [^js e]
                                         (when (= "Enter" (.-key e))
                                           (.preventDefault e)
                                           (save!)))})
          ;; A plain `datalist` and not a combo box: the browser's own is a
          ;; field somebody can type anything into, which is what this is,
          ;; and it costs no component and no keyboard handling of its own.
          ($ mui/TextField {:label "Group"
                            :value group
                            :size "small"
                            :fullWidth true
                            :placeholder "None"
                            :slotProps (sx {:htmlInput {:spellCheck false
                                                        :autoCapitalize "words"
                                                        :list (str "groups-" id)}})
                            :onChange (fn [^js e] (set-group (-> e .-target .-value)))
                            :onKeyDown (fn [^js e]
                                         (when (= "Enter" (.-key e))
                                           (.preventDefault e)
                                           (save!)))})
          ($ :datalist {:id (str "groups-" id)}
             (for [g groups]
               ($ :option {:key g :value g})))
          ;; Which feed this is, under a name that says nothing about that:
          ;; the address it is fetched from, and when that last worked.
          ($ Note (str (:feed/url feed)
                       (when-let [t (u/stamp (:feed/fetched feed))]
                         (str " · " t))))
          (when-let [e (:feed/error feed)]
            ($ mui/Alert {:severity "warning" :variant "outlined"} e)))
       ($ mui/DialogActions
          ($ mui/Button {:color "error"
                         :onClick (fn [_]
                                    (if sure?
                                      (do (state/remove-feed! id) (close!))
                                      (set-sure true)))}
             (if sure? "Really remove" "Remove"))
          ($ :span {:style {:flex "1"}})
          ($ mui/Button {:onClick (fn [_] (close!))} "Cancel")
          ($ mui/Button {:variant "contained" :disableElevation true
                         :onClick (fn [_] (save!))}
             "Save")))))

;;; ------------------------------------------------------- all feeds at once

(def-ui RemoveAllFeeds
  "Unsubscribing from everything. A dialog and not two presses of one
  button, unlike [Remove] on a single feed: a button that turns into its own
  question is enough when the mistake costs one subscription, and not when it
  costs the collection. So the question is laid over the page, it says how
  many feeds it means, and the press that does it says the number too - a
  label that names what is about to happen cannot be pressed by somebody who
  thought it said something else.

  Cancel is the emphasised button. There is no safe action in this dialog to
  put on the right, so the safe way *out* of it is what sits where the thumb
  expects to find the one it usually wants.

  It says where the collection can be got back from, because it can: an OPML
  export made a minute before this is a full undo, and afterwards there is
  none. What is really lost is what has been read - the marks go with the
  subscriptions - and that is what the note is careful to say out loud."
  [feeds busy?]
  (let [n (count feeds)]
    ($ Frame {:title "Remove all feeds"}
       ($ Body
          ($ mui/Alert {:severity "warning" :variant "outlined"}
             (str "All " n " feed" (when-not (= 1 n) "s") " go, and what you have read in them goes with them."))
          ($ Note "Export the collection first if you might want it back - an OPML file brings the feeds back, but not the marks. Articles nobody else here reads are dropped from the server."))
       ($ mui/DialogActions
          ($ mui/Button {:color "error"
                         :disabled busy?
                         :onClick (fn [_] (state/remove-all-feeds!))}
             (str "Remove all " n))
          ($ :span {:style {:flex "1"}})
          ($ mui/Button {:variant "contained" :disableElevation true
                         :autoFocus true
                         :onClick (fn [_] (close!))}
             "Cancel")))))

;;; --------------------------------------------------------- marking read

(def ^:private ages
  "The three ages somebody means by \"I am never going to read these\", in
  seconds, narrowest first.

  Three words and not a number to type: a day, a week and a month are the
  whole of what anybody has ever meant here, and a field asking for hours
  would be a setting pretending to be a decision. A month is thirty days,
  which is what a month is when nobody said which one."
  [["Older than a day"   86400]
   ["Older than a week"  604800]
   ["Older than a month" 2592000]])

(def-ui Choice
  "One of the four presses, as a line rather than a label in the middle of a
  button: they are read down the list and compared, and centred text in a
  stack of four is four things that do not line up."
  [label busy? danger? on-click]
  ($ mui/Button {:variant "outlined"
                 :disabled busy?
                 :color (if danger? "error" "inherit")
                 :onClick (fn [_] (on-click))
                 :sx (sx {:justifyContent "flex-start"
                          :borderColor "divider"
                          :fontFamily :inherit :textTransform "none"
                          :fontSize ".95rem" :padding ".55rem .75rem"})}
     label))

(def-ui MarkRead
  "Which of a feed is read: everything, or everything over a day, a week or
  a month old.

  [Mark read] used to be the press itself, and it was the one press in this
  reader that asked nothing and could not be taken back. Now it is the
  question, which is what it always was - \"mark read\" never said which ones,
  and the answer somebody wants is usually not all of them. The age ones are
  the reason it is worth a dialog rather than a confirmation: a feed that has
  got away over a holiday is cleared back to what was published this week,
  which is the thing people actually do by hand, forty rows at a time.

  Everything sits apart at the foot, in the colour of a thing that cannot be
  undone, because it is the only one of the four that reaches what has not
  been looked at yet. The three ages all spare today.

  It says which list it means - a feed, a group, or all of them - and not
  how many articles that is. The window
  holds the list it is showing - filtered, and cut at two hundred - so any
  number it put on these buttons would be the number it happens to have
  rather than the number the server would mark, and a count that is wrong is
  worse than no count at all.

  Cancel is the emphasised button, as in `RemoveAllFeeds`: there is no safe
  action in this dialog to put on the right, so the safe way out of it is
  what sits where the thumb goes."
  [where busy? narrow?]
  (let [mark! (fn [secs] (state/mark-feed-read! narrow? secs))]
    ($ Frame {:title "Mark read"}
       ($ Body
          ($ Note (str "In " where "."))
          (for [[label secs] ages]
            ($ Choice {:key secs :label label :busy? busy?
                       :on-click (fn [] (mark! secs))}))
          ($ Choice {:label "Everything" :busy? busy? :danger? true
                     :on-click (fn [] (mark! nil))}))
       ($ mui/DialogActions
          ($ mui/Button {:variant "contained" :disableElevation true
                         :autoFocus true
                         :onClick (fn [_] (close!))}
             "Cancel")))))

;;; ------------------------------------------------------------ add reader

(def-ui AddReader
  "Making somebody an account. No mail goes out - see
  `leser.api/post-admin-reader`: a message saying an account has been made
  for somebody who never asked for one is spam sent by a helpful server. The
  address is told by whoever added it.

  It stays open after one is added, with the field emptied and the answer
  under it, because adding two in a row is the normal case."
  []
  (let [[email set-email] (kc/use-state "")
        [said set-said]   (kc/use-state nil)
        ok? (address? email)
        add! (fn []
               (when ok?
                 (set-said nil)
                 (-> (state/add-reader! (str/trim email))
                     (.then (fn [answer]
                              (when answer
                                (set-email "")
                                (set-said (if (:new? answer)
                                            (str (:reader answer) " can sign in now.")
                                            (str (:reader answer) " was already here.")))))))))]
    ($ Frame {:title "Add reader"}
       ($ Body
          ($ mui/TextField {:label "E-Mail"
                            :value email
                            :size "small"
                            :fullWidth true
                            :autoFocus true
                            :autoComplete "off"
                            :slotProps (sx {:htmlInput {:inputMode "email"
                                                        :autoCapitalize "none"
                                                        :spellCheck false}})
                            :onChange (fn [^js e] (set-email (-> e .-target .-value)))
                            :onKeyDown (fn [^js e]
                                         (when (and (= "Enter" (.-key e)) ok?)
                                           (.preventDefault e)
                                           (add!)))})
          ($ Note (or said
                      "They sign in with a code at that address, like everybody else.")))
       ($ mui/DialogActions
          ($ mui/Button {:onClick (fn [_] (close!))} "Close")
          ($ mui/Button {:variant "contained" :disableElevation true
                         :disabled (not ok?)
                         :onClick (fn [_] (add!))}
             "Add reader")))))

;;; ------------------------------------------------------------------- all

(def-ui Dialogs
  "Whichever one is open, over everything else. One at a time: a dialog over
  a dialog is a program that lost track of what it was asking."
  []
  (let [{:keys [dialog feeds feed group busy?]} (state/use-state)
        narrow? (mui/use-narrow?)
        ;; The groups already in use, for the field in `Feed`. Read off the
        ;; feeds the window already has: there is no list of groups anywhere
        ;; to ask for, on this side or the other one.
        groups  (->> feeds (keep :feed/group) distinct sort vec)]
    (cond
      (= :add-feed dialog)        ($ AddFeed {:busy? busy?})
      (= :remove-all-feeds dialog) ($ RemoveAllFeeds {:feeds feeds :busy? busy?})
      (= :add-reader dialog)      ($ AddReader)
      ;; `narrow?` because marking read ends on the front page, and which
      ;; page that is depends on the window - the same argument [Home] is
      ;; given, for the same reason.
      (= :mark-read dialog)
      ($ MarkRead {:where (cond
                            feed  (u/feed-label
                                   (some #(when (= feed (:feed/id %)) %) feeds))
                            group group
                            :else "every feed")
                   :busy? busy? :narrow? narrow?})
      (and (vector? dialog) (= :feed (first dialog)))
      (when-let [f (first (filter #(= (second dialog) (:feed/id %)) feeds))]
        ($ Feed {:feed f :groups groups}))
      :else nil)))
